import { H3, Small, theme } from "@upsolve/ui";
import React from "react";
import styled from "styled-components";
import ThumbsUp from "./images/thumbs-up.svg";
import ThumbsDown from "./images/thumbs-down.svg";
import QuestionMark from "./QuestionMark.svg";
import { TOptionInfo } from "./ResultsComparison";
import { formatMonths } from "./formatMonths";
import { Tooltip } from "./Tooltip";
import { DetailHeading, DetailDescriptionCentered } from "./Components";

type TStatusQuoInfo = {
  interest: number;
  totalPayment: number;
  monthlyPayment: number;
  monthsInRepayment: number;
};

const IneligibleMessageBox = styled.div`
  font-size: 14px;
  text-align: start;
  width: 100%;
  margin-bottom: 1em;
  background-color: #f5f5f8;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  .title {
    text-align: center;
  }
`;

export const DetailCard = (props: {
  optionInfo: TOptionInfo;
  unsecuredDebtEstimate: number;
  availableMonthlyFunds: number;
  statusQuoInfo: TStatusQuoInfo;
  onClickBack: () => void;
}) => {
  return (
    <StyledDetailCardContainer>
      <StyledDetailCard isEligible={props.optionInfo.isEligible}>
        <button className="back" onClick={props.onClickBack} data-test-label="back-button">
          Back
        </button>
        <div style={{ marginTop: "-80px" }}>
          <StyledDetailCardThumbContainer>
            {props.optionInfo.isEligible ? (
              <ThumbsUp />
            ) : props.optionInfo.isEligible !== false ? (
              <QuestionMark />
            ) : (
              <ThumbsDown />
            )}
          </StyledDetailCardThumbContainer>
          <div className="title_section section">
            <DetailHeading>{props.optionInfo.title}</DetailHeading>
            <DetailDescriptionCentered>{props.optionInfo.description}</DetailDescriptionCentered>
            {
              <div className="actions_section">
                {props.optionInfo.notEligibleMessage && (
                  <IneligibleMessageBox>{props.optionInfo.notEligibleMessage}</IneligibleMessageBox>
                )}
                <div className="button-container">{props.optionInfo.actions}</div>
              </div>
            }
          </div>
        </div>
        {props.optionInfo.isEligible !== false && (
          <div className="section">
            <div className="section-title">Summary</div>
            <div>
              {props.optionInfo.savings > 0 && (
                <div>
                  Savings: <b>${props.optionInfo.savings.toLocaleString()} </b>
                </div>
              )}
              <div>
                {props.optionInfo.debtTimelineBreakdown.fieldName}:{" "}
                <b>{props.optionInfo.debtTimelineBreakdown.duration}</b>
              </div>
              <div>
                Time to set up: <b>{props.optionInfo.timeToSetUp}</b>
              </div>
            </div>
            {props.optionInfo.debtTimelineBreakdown.details && <i>{props.optionInfo.debtTimelineBreakdown.details}</i>}
          </div>
        )}

        <div className="good_for_section section">
          <b className="section-title">Who is it good for?</b>
          <div>{props.optionInfo.goodForStatement}</div>
        </div>
        <div className="affordability_section section">
          <b className="section-title">
            Can you afford it?{" "}
            {props.optionInfo.canAfford ? "👍" : props.optionInfo.canAfford === undefined ? <></> : "👎"}
          </b>
          <div>{props.optionInfo.canAffordMessage}</div>
        </div>

        {props.optionInfo.isEligible !== false && (
          <div className="savings_section section">
            <b className="section-title">Savings breakdown</b>
            <div className="estimated_debt">
              <b className="title">Without {props.optionInfo.optionName}</b>
              <div className="info-row">
                <div>Principle</div>
                <div>
                  <b>${props.unsecuredDebtEstimate.toLocaleString()}</b>
                </div>
              </div>

              {props.statusQuoInfo.totalPayment === 0 ? (
                <div>
                  Your available monthly funds of ${props.availableMonthlyFunds.toLocaleString()} probably cannot cover
                  the interest on this debt. This means you may not be able to pay it off.
                </div>
              ) : (
                <>
                  <div className="info-row">
                    <div>
                      + estimated interest at&nbsp;
                      <Tooltip
                        content={
                          <div>
                            Based on the average&nbsp;
                            <a target="_blank" href="https://www.federalreserve.gov/releases/g19/current/">
                              credit card interest rate
                            </a>
                            &nbsp;in 2024
                          </div>
                        }
                      >
                        <span style={{ textDecoration: "underline" }}>23%</span>
                      </Tooltip>
                    </div>
                    <div className="payment">
                      <b>+ ${Math.round(props.statusQuoInfo.interest).toLocaleString()}</b>
                    </div>
                  </div>
                  <div className="info-row">
                    <div>= estimated total payment</div>
                    <div className="red payment">
                      <b>= ${Math.round(props.statusQuoInfo.totalPayment).toLocaleString()}</b>
                    </div>
                  </div>
                  <div>
                    <hr />
                  </div>
                  <div className="info-row">
                    <div>Estimated timeline</div>
                    <div className="red payment">
                      <b>{formatMonths(props.statusQuoInfo.monthsInRepayment)}</b>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="estimated_debt">
              <b className="title">With {props.optionInfo.optionName}</b>
              <div className="info-row">
                <div>Principle</div>
                <div className="payment">
                  <b>${props.unsecuredDebtEstimate.toLocaleString()}</b>
                </div>
              </div>

              {props.optionInfo.savingsBreakdown.map((savingsBreakdownRow) => (
                <div className="info-row" key={savingsBreakdownRow.item}>
                  <div>{savingsBreakdownRow.item}</div>
                  <div className="payment">
                    <b>{savingsBreakdownRow.value}</b>
                  </div>
                </div>
              ))}
              <div className="info-row">
                <div>= estimated total payment</div>
                <div className="green payment">
                  <b>= ${Math.round(props.optionInfo.totalPayment).toLocaleString()}</b>
                </div>
              </div>
              <div>
                <hr />
              </div>
              <div className="info-row">
                <div>{props.optionInfo.debtTimelineBreakdown.fieldName}</div>
                <div className="green payment">
                  <b>{props.optionInfo.debtTimelineBreakdown.duration}</b>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="actions_section">
          <div className="button-container-bottom">{props.optionInfo.actions}</div>
        </div>
      </StyledDetailCard>
    </StyledDetailCardContainer>
  );
};
const StyledDetailCardContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: auto;
`;
const StyledDetailCard = styled.div<{ isEligible: boolean | undefined }>`
  display: flex;
  flex-direction: column;
  text-align: center;
  flex: 1;
  padding: 20px;
  max-width: 600px;
  gap: 24px;
  .back {
    color: black;
    background-color: ${theme.colors.white[700]};
    font-size: 14px;
    font-weight: 500;
    border: none;
    border-radius: 12px;
    padding: 10px 24px;
    cursor: pointer;
    width: 20vw;
    max-width: 100px;
    margin-top: 30px;
    z-index: 99;
  }
  .section {
    padding: 40px 40px 40px;
    background: #ffffff;
    box-shadow: 0px 24px 24px rgba(0, 0, 0, 0.05), 0px 16px 24px rgba(0, 0, 0, 0.05);
    border-radius: 16px;
    text-align: start;
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-size: 14px;
    .section-title {
      text-transform: uppercase;
      opacity: 80%;
      color: ${theme.colors.gray[500]};
      font-weight: 700;
    }
  }
  .title_section {
    border-radius: 8px;
    padding-top: 40px;
    padding-bottom: 40px;
    box-sizing: border-box;
    display: flex;
    gap: 0px;
    flex-direction: column;
    text-align: center;
  }
  .savings_section {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    .estimated_debt {
      display: flex;
      flex-direction: column;
    }
    .title {
      margin-bottom: 8px;
    }
    .payment {
      display: flex;
      flex: 1;
      justify-content: flex-end;
      align-items: center;
    }
  }
  .good_for_section {
  }
  .affordability_section {
  }
  .actions_section {
    width: 100%;
    display: flex;
    flex-direction: column;
    .button-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .button-container-bottom {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 60px;
    }
  }

  .bottom_padding {
    padding: 20px;
  }

  .red {
    color: ${theme.colors.red[300]};
  }
  .green {
    color: ${theme.colors.green[300]};
  }
  .gray {
    color: ${theme.colors.gray[500]};
  }
  .info-row {
    display: flex;
    justify-content: space-between;
  }
  .info-row + .info-row {
    border-top: 1px dashed ${(props) => props.theme.colors.white[300]};
    margin-top: 5px;
    padding-top: 5px;
  }
`;

const StyledDetailCardThumbContainer = styled.div`
  position: relative;
  top: 38px;
`;
